import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import './HRLocations.css';

const CITIES = ['Lahore', 'Karachi', 'Islamabad', 'Multan'];
const CITY_FILTERS = ['All Cities', ...CITIES];

const SHIFTS = [
  { id: 1, name: 'Slot 1 — Standard (10:00–18:00)' },
  { id: 2, name: 'Slot 2 — Extended (10:00–19:00)' },
  { id: 3, name: 'Slot 3 — Evening (11:00–20:00)' },
];

const EMPTY_FORM = {
  name: '', address: '', city: 'Lahore',
  gps_lat: '', gps_lng: '', geofence_radius: '50',
  wifi_ssid: '', wifi_bssid: '',
  shift_start: '10:00', shift_end: '18:00',
  grace_period_minutes: '20',
};

function formatBSSID(bssid) {
  if (!bssid) return '—';
  return bssid.toUpperCase();
}

function getBSSIDStatus(bssid) {
  if (!bssid) return { label: 'Not Set', cls: 'bssid-none' };
  return { label: 'Configured', cls: 'bssid-ok' };
}

function getCityColor(city) {
  const map = {
    Lahore:    'loc-city-lahore',
    Karachi:   'loc-city-karachi',
    Islamabad: 'loc-city-islamabad',
    Multan:    'loc-city-multan',
  };
  return map[city] || 'loc-city-lahore';
}

// ─── LOCATION MODAL ───────────────────────────────────────────────────────────

function LocationModal({ mode, location, onSave, onClose }) {
  const [form, setForm] = useState(
    mode === 'edit' && location
      ? {
          name:                 location.name                 || '',
          address:              location.address              || '',
          city:                 location.city                 || 'Lahore',
          gps_lat:              location.gps_lat              || '',
          gps_lng:              location.gps_lng              || '',
          geofence_radius:      location.geofence_radius      || '50',
          wifi_ssid:            location.wifi_ssid            || '',
          wifi_bssid:           location.wifi_bssid           || '',
          shift_start:          location.shift_start          || '10:00',
          shift_end:            location.shift_end            || '18:00',
          grace_period_minutes: location.grace_period_minutes || '20',
        }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim())    { setError('Location name is required'); return; }
    if (!form.city)           { setError('City is required'); return; }
    if (!form.gps_lat || !form.gps_lng) { setError('GPS coordinates are required'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        gps_lat:              parseFloat(form.gps_lat),
        gps_lng:              parseFloat(form.gps_lng),
        geofence_radius:      parseInt(form.geofence_radius) || 50,
        grace_period_minutes: parseInt(form.grace_period_minutes) || 20,
      };

      let res;
      if (mode === 'add') {
        res = await api.createLocation(payload);
      } else {
        res = await api.updateLocation(location.id, payload);
      }
      if (res.error) { setError(res.error); return; }
      onSave();
    } catch (err) {
      setError('Failed to save location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="loc-modal-overlay" onClick={onClose}>
      <div className="loc-modal" onClick={e => e.stopPropagation()}>
        <div className="loc-modal-header">
          <h2>{mode === 'add' ? 'Add New Location' : 'Edit Location'}</h2>
          <button className="loc-modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="loc-modal-error">{error}</div>}

        <div className="loc-modal-body">

          {/* Basic Info */}
          <div className="loc-form-section">Basic Info</div>
          <div className="loc-form-grid">
            <div className="loc-form-group loc-span-2">
              <label>Location Name *</label>
              <input type="text" placeholder="e.g. DHA Phase 3 - Fotile Showroom"
                value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div className="loc-form-group loc-span-2">
              <label>Address</label>
              <input type="text" placeholder="Full street address"
                value={form.address} onChange={e => set('address', e.target.value)} />
            </div>
            <div className="loc-form-group">
              <label>City *</label>
              <select value={form.city} onChange={e => set('city', e.target.value)}>
                {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="loc-form-group">
              <label>Geofence Radius (meters)</label>
              <input type="number" placeholder="50"
                value={form.geofence_radius}
                onChange={e => set('geofence_radius', e.target.value)} />
            </div>
          </div>

          {/* GPS */}
          <div className="loc-form-section">GPS Coordinates *</div>
          <div className="loc-form-tip">
            Open Google Maps, right-click the location, copy the coordinates.
          </div>
          <div className="loc-form-grid">
            <div className="loc-form-group">
              <label>Latitude</label>
              <input type="number" step="0.000001" placeholder="e.g. 31.5204"
                value={form.gps_lat} onChange={e => set('gps_lat', e.target.value)} />
            </div>
            <div className="loc-form-group">
              <label>Longitude</label>
              <input type="number" step="0.000001" placeholder="e.g. 74.3587"
                value={form.gps_lng} onChange={e => set('gps_lng', e.target.value)} />
            </div>
          </div>

          {/* WiFi */}
          <div className="loc-form-section">WiFi Configuration</div>
          <div className="loc-form-tip">
            BSSID is the router's MAC address — found on the router label or admin panel.
            Format: XX:XX:XX:XX:XX:XX
          </div>
          <div className="loc-form-grid">
            <div className="loc-form-group">
              <label>WiFi SSID (Network Name)</label>
              <input type="text" placeholder="e.g. Fotile-Office"
                value={form.wifi_ssid} onChange={e => set('wifi_ssid', e.target.value)} />
            </div>
            <div className="loc-form-group">
              <label>WiFi BSSID (Router MAC) *</label>
              <input type="text" placeholder="e.g. A8:63:7D:96:F7:83"
                value={form.wifi_bssid}
                onChange={e => set('wifi_bssid', e.target.value.toUpperCase())} />
            </div>
          </div>

          {/* Shift timing */}
          <div className="loc-form-section">Shift Timing</div>
          <div className="loc-form-grid">
            <div className="loc-form-group">
              <label>Shift Start</label>
              <input type="time" value={form.shift_start}
                onChange={e => set('shift_start', e.target.value)} />
            </div>
            <div className="loc-form-group">
              <label>Shift End</label>
              <input type="time" value={form.shift_end}
                onChange={e => set('shift_end', e.target.value)} />
            </div>
            <div className="loc-form-group">
              <label>Grace Period (minutes)</label>
              <input type="number" placeholder="20"
                value={form.grace_period_minutes}
                onChange={e => set('grace_period_minutes', e.target.value)} />
            </div>
          </div>

        </div>

        <div className="loc-modal-footer">
          <button className="loc-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="loc-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : mode === 'add' ? 'Add Location' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LOCATION CARD ────────────────────────────────────────────────────────────

function LocationCard({ loc, onEdit }) {
  const bssidStatus = getBSSIDStatus(loc.wifi_bssid);

  return (
    <div className="loc-card">
      <div className="loc-card-header">
        <div className="loc-card-title-row">
          <span className={`loc-city-dot ${getCityColor(loc.city)}`} />
          <div className="loc-card-name-wrap">
            <span className="loc-card-name">{loc.name}</span>
            <span className="loc-card-city">{loc.city}</span>
          </div>
        </div>
        <div className="loc-card-actions">
          <span className="loc-emp-count">
            {loc.employee_count} {parseInt(loc.employee_count) === 1 ? 'employee' : 'employees'}
          </span>
          <button className="loc-edit-btn" onClick={() => onEdit(loc)}>Edit</button>
        </div>
      </div>

      {loc.address && (
        <div className="loc-address">{loc.address}</div>
      )}

      <div className="loc-details">
        {/* WiFi */}
        <div className="loc-detail-row">
          <span className="loc-detail-label">WiFi BSSID</span>
          <div className="loc-detail-value-row">
            <span className="loc-bssid">{formatBSSID(loc.wifi_bssid)}</span>
            <span className={`loc-bssid-status ${bssidStatus.cls}`}>{bssidStatus.label}</span>
          </div>
        </div>

        {loc.wifi_ssid && (
          <div className="loc-detail-row">
            <span className="loc-detail-label">WiFi SSID</span>
            <span className="loc-detail-value">{loc.wifi_ssid}</span>
          </div>
        )}

        {/* GPS */}
        <div className="loc-detail-row">
          <span className="loc-detail-label">GPS Coordinates</span>
          <span className="loc-detail-value loc-mono">
            {loc.gps_lat}, {loc.gps_lng}
          </span>
        </div>

        {/* Geofence */}
        <div className="loc-detail-row">
          <span className="loc-detail-label">Geofence Radius</span>
          <span className="loc-detail-value">{loc.geofence_radius}m</span>
        </div>

        {/* Shift */}
        <div className="loc-detail-row">
          <span className="loc-detail-label">Shift Hours</span>
          <span className="loc-detail-value">
            {loc.shift_start
              ? `${loc.shift_start} — ${loc.shift_end}`
              : '—'}
          </span>
        </div>

        {/* Grace */}
        <div className="loc-detail-row">
          <span className="loc-detail-label">Grace Period</span>
          <span className="loc-detail-value">{loc.grace_period_minutes} minutes</span>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function HRLocations() {
  const [locations, setLocations]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [cityFilter, setCityFilter] = useState('All Cities');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editLocation, setEditLocation] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getAllLocations();
      setLocations(res.locations || []);
      setError(null);
    } catch (err) {
      setError('Failed to load locations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = cityFilter === 'All Cities'
    ? locations
    : locations.filter(l => l.city === cityFilter);

  // Group by city
  const grouped = {};
  filtered.forEach(loc => {
    if (!grouped[loc.city]) grouped[loc.city] = [];
    grouped[loc.city].push(loc);
  });

  const totalEmployees = locations.reduce((s, l) => s + parseInt(l.employee_count || 0), 0);
  const bssidMissing   = locations.filter(l => !l.wifi_bssid).length;

  return (
    <div className="loc-root">

      {/* Header */}
      <div className="loc-header">
        <div className="loc-header-left">
          <h1 className="loc-title">Locations</h1>
          <span className="loc-subtitle">
            {locations.length} locations · {totalEmployees} employees assigned
          </span>
        </div>
        <button className="loc-add-btn" onClick={() => setShowAddModal(true)}>
          + Add Location
        </button>
      </div>

      {/* Stats */}
      <div className="loc-stats">
        <div className="loc-stat-card">
          <span className="loc-stat-num">{locations.length}</span>
          <span className="loc-stat-lbl">Total Locations</span>
        </div>
        {CITIES.map(city => (
          <div key={city} className="loc-stat-card">
            <span className="loc-stat-num">
              {locations.filter(l => l.city === city).length}
            </span>
            <span className="loc-stat-lbl">{city}</span>
          </div>
        ))}
        {bssidMissing > 0 && (
          <div className="loc-stat-card loc-stat-warn">
            <span className="loc-stat-num">{bssidMissing}</span>
            <span className="loc-stat-lbl">WiFi Not Set</span>
          </div>
        )}
      </div>

      {/* Alert if BSSID missing */}
      {bssidMissing > 0 && (
        <div className="loc-alert">
          ⚠️ {bssidMissing} location{bssidMissing > 1 ? 's' : ''} missing WiFi BSSID —
          employees at {bssidMissing > 1 ? 'these locations' : 'this location'} cannot clock in.
        </div>
      )}

      {/* City filter */}
      <div className="loc-filters">
        {CITY_FILTERS.map(c => (
          <button
            key={c}
            className={`loc-filter-btn ${cityFilter === c ? 'loc-filter-active' : ''}`}
            onClick={() => setCityFilter(c)}
          >{c}</button>
        ))}
      </div>

      {error && <div className="loc-error">{error}</div>}

      {loading ? (
        <div className="loc-loading">
          <div className="loc-spinner" />
          <p>Loading locations…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="loc-empty">No locations found.</div>
      ) : (
        <div className="loc-content">
          {Object.entries(grouped).map(([city, locs]) => (
            <div key={city} className="loc-city-group">
              <div className="loc-city-label">
                <span className={`loc-city-dot ${getCityColor(city)}`} />
                {city}
                <span className="loc-city-count">{locs.length}</span>
              </div>
              <div className="loc-cards-grid">
                {locs.map(loc => (
                  <LocationCard
                    key={loc.id}
                    loc={loc}
                    onEdit={setEditLocation}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add modal */}
      {showAddModal && (
        <LocationModal
          mode="add"
          onSave={() => { setShowAddModal(false); fetchData(); }}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Edit modal */}
      {editLocation && (
        <LocationModal
          mode="edit"
          location={editLocation}
          onSave={() => { setEditLocation(null); fetchData(); }}
          onClose={() => setEditLocation(null)}
        />
      )}
    </div>
  );
}